import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import StatusBadge from "@/components/StatusBadge";
import SyncIndicator from "@/components/SyncIndicator";
import { 
  FileText, 
  Plus, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ChevronRight,
  Users,
  User,
  LogOut,
  Key,
  HelpCircle,
  Edit,
  MoreVertical,
  Upload,
  Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyDashboard = () => {
  const { user, logout } = useAuth();
  const { isOnline } = useOffline();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentClaims, setRecentClaims] = useState([]);
  const [draftClaims, setDraftClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCompanyLogo();
  }, []);

  const fetchCompanyLogo = async () => {
    try {
      const res = await axios.get(`${API}/company/logo`);
      if (res.data.logo) setCompanyLogo(res.data.logo);
      if (res.data.name) setCompanyName(res.data.name);
    } catch (error) {
      // Logo not set yet
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image (PNG ou JPG)");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }
    
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        try {
          await axios.post(`${API}/company/logo`, { logo: base64 });
          setCompanyLogo(base64);
          toast.success("Logo mis à jour avec succès");
        } catch (error) {
          toast.error("Erreur lors de l'upload du logo");
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Erreur lors de la lecture du fichier");
      setUploadingLogo(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, claimsRes] = await Promise.all([
        axios.get(`${API}/company/stats`),
        axios.get(`${API}/claims`),
      ]);
      setStats(statsRes.data);
      const items = claimsRes.data.items || claimsRes.data;
      setRecentClaims(items.slice(0, 5));
      setDraftClaims(items.filter(c => c.status === 'draft').slice(0, 3));
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const statCards = [
    {
      title: "En cours",
      value: stats?.in_progress_claims || 0,
      icon: Clock,
      color: "bg-sky-600",
    },
    {
      title: "Brouillons",
      value: stats?.draft_claims || 0,
      icon: AlertCircle,
      color: "bg-orange-500",
    },
    {
      title: "Terminés",
      value: stats?.completed_claims || 0,
      icon: CheckCircle,
      color: "bg-green-500",
    },
    {
      title: "Total",
      value: stats?.total_claims || 0,
      icon: FileText,
      color: "bg-slate-600",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="company-dashboard">
      {/* Header */}
      <header className="bg-sky-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {companyLogo && (
                <img 
                  src={companyLogo} 
                  alt="Logo entreprise" 
                  className="h-10 w-10 rounded-lg object-contain bg-white p-1"
                  data-testid="company-logo-header"
                />
              )}
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
                  Bonjour, {user?.first_name}
                </h1>
                <p className="text-sky-100 text-sm mt-1">
                  {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {/* New Report Button */}
              <Button
                onClick={() => navigate("/claims/new")}
                className="bg-white text-sky-600 hover:bg-sky-50 h-10 sm:h-12 px-3 sm:px-6 font-semibold"
                data-testid="new-claim-btn"
              >
                <Plus className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">Nouveau dossier</span>
              </Button>
              
              {/* Account Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="h-10 sm:h-12 px-2 sm:px-3 text-white hover:bg-sky-500"
                    data-testid="account-menu-btn"
                  >
                    <User className="w-5 h-5" />
                    <span className="hidden sm:inline ml-2">{user?.first_name}</span>
                    <MoreVertical className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium text-slate-900">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem 
                    onClick={() => navigate("/account")}
                    className="cursor-pointer"
                    data-testid="menu-my-account"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Mon compte
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate("/account")}
                    className="cursor-pointer"
                    data-testid="menu-change-password"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Changer le mot de passe
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index} className="shadow-sm" data-testid={`stat-${card.title.toLowerCase()}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={`${card.color} rounded-lg p-2`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {loading ? "..." : card.value}
                      </p>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">
                        {card.title}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {user?.role === "company" && (
            <Button
              variant="outline"
              onClick={() => navigate("/users")}
              className="h-16 justify-start text-left"
              data-testid="quick-users"
            >
              <Users className="w-6 h-6 mr-3 flex-shrink-0 text-sky-600" />
              <div>
                <div className="font-semibold text-slate-700">Équipe</div>
                <div className="text-xs text-slate-500">Gérer les techniciens</div>
              </div>
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={() => navigate("/help")}
            className="h-16 justify-start text-left"
            data-testid="quick-help"
          >
            <HelpCircle className="w-6 h-6 mr-3 flex-shrink-0 text-slate-500" />
            <div>
              <div className="font-semibold text-slate-700">Aide / Support</div>
              <div className="text-xs text-slate-500">Assistance et contact</div>
            </div>
          </Button>
        </div>

        {/* Branding Section */}
        {user?.role === "company" && (
          <Card className="mb-6" data-testid="branding-section">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-sky-600" />
                Branding de l'entreprise
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="flex-shrink-0">
                  {companyLogo ? (
                    <img 
                      src={companyLogo} 
                      alt="Logo entreprise" 
                      className="w-24 h-24 rounded-lg object-contain border-2 border-slate-200 p-2 bg-white"
                      data-testid="company-logo-preview"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-3">
                    Votre logo apparaîtra dans l'en-tête de l'application et sur les rapports PDF générés.
                  </p>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      disabled={uploadingLogo}
                      onClick={(e) => {
                        e.preventDefault();
                        e.currentTarget.parentElement.querySelector('input[type="file"]').click();
                      }}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <div className="spinner w-4 h-4 border-sky-600 border-t-transparent mr-2" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {companyLogo ? "Changer le logo" : "Uploader un logo"}
                    </Button>
                  </label>
                  <p className="text-xs text-slate-400 mt-2">PNG ou JPG, max 5 Mo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two column layout for drafts and recent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Draft Claims - Resume Work */}
          {draftClaims.length > 0 && (
            <Card className="border-orange-200 bg-orange-50/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Edit className="w-5 h-5 text-orange-500" />
                    Brouillons à reprendre
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {draftClaims.map((claim) => (
                    <div
                      key={claim.id}
                      onClick={() => navigate(`/claims/${claim.id}/edit`)}
                      className="bg-white rounded-lg border border-orange-200 p-3 cursor-pointer hover:shadow-md transition-shadow"
                      data-testid={`draft-card-${claim.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">
                            {claim.client_name || "Sans nom"}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {claim.address || "Adresse non renseignée"}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" className="text-orange-600 hover:text-orange-700">
                          Reprendre
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Claims */}
          <Card className={draftClaims.length === 0 ? "lg:col-span-2" : ""}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Dossiers récents</CardTitle>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/claims")}
                  className="text-sky-600 hover:text-sky-700"
                  data-testid="see-all-claims"
                >
                  Voir tout
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : recentClaims.length > 0 ? (
                <div className="space-y-3">
                  {recentClaims.map((claim) => (
                    <div
                      key={claim.id}
                      onClick={() => navigate(`/claims/${claim.id}`)}
                      className="claim-card cursor-pointer"
                      data-testid={`claim-card-${claim.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-900 truncate">
                              {claim.file_number}
                            </span>
                            <StatusBadge status={claim.status} />
                          </div>
                          <p className="text-sm text-slate-600 truncate">
                            {claim.client_name}
                          </p>
                          <p className="text-xs text-slate-400 truncate">
                            {claim.address}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">Aucun dossier créé</p>
                  <Button
                    onClick={() => navigate("/claims/new")}
                    className="mt-4 bg-sky-600 hover:bg-sky-700"
                    data-testid="empty-create-claim"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer mon premier dossier
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <SyncIndicator />
    </div>
  );
};

export default CompanyDashboard;
